import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  BarChart,
  Card,
  CardHeader,
  CopyField,
  DataTable,
  EmptyState,
  ErrorState,
  Grid,
  Icon,
  KpiCard,
  PageHeader,
  SegmentedControl,
  TableCard,
} from '@sparkery/ui';
import type { RangeKey, ReportResponse } from '@sparkery/contracts';
import { useViewer } from '../../lib/viewer.tsx';
import { useRefer } from '../../lib/workspace.ts';
import {
  RANGE_LABEL,
  RANGE_OPTIONS,
  conversionText,
  longDate,
  moneyShort,
  orderColumns,
  peakOf,
  ratePercent,
  trimEmptyEnds,
  useReport,
} from '../../lib/report.tsx';
import './dashboard.css';

export function DashboardView() {
  const { viewer } = useViewer();
  const [range, setRange] = useState<RangeKey>('today');
  const report = useReport({ range });
  // Only a partner session has one link and one code to promote; the whole-programme
  // view has neither, and /refer refuses a request without a partner.
  const refer = useRefer({ enabled: Boolean(viewer.partner) });

  const data = report.data;
  // Until the report arrives, assume commission IS shown: a column that appears a
  // moment after the table would reflow it, which reads as a glitch. A fixed-fee
  // partner sees it withdrawn once, on first load, and never again from cache.
  const showsCommission = data?.showsCommission ?? true;

  /**
   * A failure with nothing cached to fall back on.
   *
   * The figures are hidden entirely rather than rendered as a row of em dashes. Those
   * dashes are not harmless: the optimistic `showsCommission` above stops being
   * transient once the request has failed, so a fixed-fee partner would be left looking
   * at a Payable tile captioned "Past its 30-day window" — a settlement window they
   * have no place in — for as long as the error persisted.
   */
  const failed = report.isError && !data;

  // Hours are trimmed to the trading day; days are not — see trimEmptyEnds.
  const bars = range === 'today' ? trimEmptyEnds(data?.salesBars ?? []) : (data?.salesBars ?? []);

  const columns = orderColumns({ showsCommission, range });

  return (
    <>
      {/* The day, not the account name: whose numbers these are is already answered by
          the sidebar, and which day it is is not answered anywhere else. */}
      <PageHeader
        title="Dashboard"
        subtitle={longDate(new Date())}
        actions={
          <SegmentedControl
            label="Date range"
            options={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
          />
        }
      />

      {report.isError ? (
        <ErrorState
          title="Could not load the dashboard"
          {...(report.error instanceof Error ? { detail: report.error.message } : {})}
          onRetry={() => void report.refetch()}
        />
      ) : null}

      {failed ? null : (
        <>
          {/* Four tiles either way. A fixed-fee partner gets Conversion rate where a
          commission partner gets Payable — they are told how well their traffic
          converts instead of a percentage they do not earn. Not five: the grid tops out
          at four tracks and a fifth would sit alone on a second row. */}
          <Grid cols={4}>
            <KpiCard
              label="Referral sales"
              value={data ? moneyShort(data.summary.sales.amount) : '—'}
              meta={RANGE_LABEL[range]}
              provisional={data?.quality.provisional ?? false}
              iconName="sales"
            />
            <KpiCard
              label="Orders"
              value={data ? String(data.summary.orderCount) : '—'}
              unit="orders"
              meta={RANGE_LABEL[range]}
              iconName="orders"
              tint="ink"
            />
            {/* PAYABLE, not "commission earned" — earned was ambiguous about whether the
            money could be invoiced. The forecast rides in the caption rather than in a
            fifth tile, and it is stated even when zero: a partner who cannot see the
            forecast at all has no way to know the figure above is only part of it.
            Reporting gives the two their own tiles side by side. */}
            {showsCommission ? (
              <KpiCard
                label="Payable"
                value={data ? moneyShort(data.summary.payable.amount) : '—'}
                {...(data?.summary.commissionRate
                  ? { unit: `at ${ratePercent(data.summary.commissionRate)}%` }
                  : {})}
                meta={payableMeta(data)}
                iconName="commission"
                tint="amber"
              />
            ) : (
              <KpiCard
                label="Conversion rate"
                value={conversionText(data?.summary.conversionRate)}
                {...(data?.summary.conversionRate === null ? {} : { unit: '%' })}
                meta={
                  data?.summary.conversionRate === null
                    ? 'Needs visitor numbers to work out'
                    : 'Orders as a share of visits'
                }
                iconName="conversion"
                tint="amber"
              />
            )}
            <KpiCard
              label="Visitors"
              value={data?.traffic ? data.traffic.sessions.toLocaleString() : '—'}
              unit="sessions"
              meta={
                data?.quality.trafficUnavailable
                  ? 'Analytics has not synced yet'
                  : 'Sessions from referral links'
              }
              iconName="visitors"
              tint="pink"
            />
          </Grid>

          {/* Chart and Refer panel share a row, as in the prototype: the two things a
          partner opens the dashboard for are today's shape and the link to send. With
          no partner in session there is no link, and the chart takes the full width. */}
          <div className={viewer.partner ? 'dashboardSplit' : undefined}>
            <Card>
              <CardHeader
                title={range === 'today' ? 'Sales by hour' : 'Sales by day'}
                meta={
                  peakOf(bars, { hourly: range === 'today' }) ??
                  (data ? `${data.summary.orderCount} orders` : undefined)
                }
              />
              <BarChart
                bars={bars}
                caption={`Referral sales, ${RANGE_LABEL[range].toLowerCase()}`}
                emptyMessage="No referral sales in this period."
              />
            </Card>

            {viewer.partner ? (
              <Card variant="dark" className="dashboardRefer">
                <CardHeader title="Refer" onDark />
                <CopyField label="Latest referral link" value={refer.data?.primaryLink ?? '—'} />
                <CopyField label="Discount code" value={refer.data?.primaryCode ?? '—'} />
                <Link to="/refer" className="dashboardReferMore">
                  More
                  <Icon name="arrow-right" size={14} />
                </Link>
              </Card>
            ) : null}
          </div>

          <TableCard
            title={
              range === 'today'
                ? 'Referral sales today'
                : `Referral sales, ${RANGE_LABEL[range].toLowerCase()}`
            }
            action={
              <Link to="/reporting" search={{ partnerId: undefined }} className="dashboardViewAll">
                View all
              </Link>
            }
          >
            <DataTable
              caption={`Referral sales, ${RANGE_LABEL[range].toLowerCase()}`}
              columns={columns}
              rows={data?.orders ?? []}
              rowKey={(row) => row.id}
              loading={report.isPending}
              empty={
                <EmptyState
                  title="No referral sales yet"
                  body={
                    range === 'today'
                      ? 'Nothing has come through today. Try a longer period.'
                      : 'No orders used a referral code in this period.'
                  }
                />
              }
            />
          </TableCard>
        </>
      )}
    </>
  );
}

/**
 * The caption under Payable.
 *
 * A missing rate no longer means "fixed fee" here — that partner has no commission
 * tiles at all — so the only remaining case is the whole-programme view, where there
 * are several rates and naming one would be wrong.
 */
function payableMeta(data: ReportResponse | undefined): string {
  if (!data) return 'Past its 30-day window';
  const forecast = `${moneyShort(data.summary.forecasted.amount)} still forecasted`;
  return data.summary.commissionRate ? forecast : `All partners · ${forecast}`;
}
